import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { Clipboard } from '@angular/cdk/clipboard';
import { Meter, ShareInfo } from '../../../models/meter.model';
import { UtilService } from '../../../services/util.service';
import { getShareableLink } from '../../../utils/meter.utils';

export interface ManageSharesDialogData {
  meter: Meter;
}

@Component({
  selector: 'app-manage-shares-dialog',
  templateUrl: './manage-shares-dialog.component.html',
  styleUrls: ['./manage-shares-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatChipsModule
  ]
})
export class ManageSharesDialogComponent {
  displayedColumns: string[] = ['shareType', 'shareeName', 'startDate', 'endDate', 'actions'];
  
  constructor(
    public dialogRef: MatDialogRef<ManageSharesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageSharesDialogData,
    private utilService: UtilService,
    private clipboard: Clipboard
  ) {}

  get meter(): Meter {
    return this.data.meter;
  }

  get shares(): ShareInfo[] {
    return (this.meter.shareeNames || []).filter(s => s.shareType === 'platform');
  }

  getShareTypeIcon(shareType: string): string {
    switch (shareType) {
      case 'platform':
        return 'business';
      case 'email':
        return 'email';
      case 'link':
        return 'link';
      default:
        return 'share';
    }
  }

  getShareTypeClass(shareType: string): string {
    return `share-type-${shareType}`;
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  onRevokeShare(share: ShareInfo): void {
    // Show confirmation popup
    this.utilService.confirmRevokeShare(share, this.meter.meterNumber).subscribe((confirmed: boolean) => {
      if (confirmed) {
        // User confirmed, close dialog and pass share info to parent
        this.dialogRef.close({ action: 'revoke', share });
      }
      // If user cancelled, dialog stays open
    });
  }

  onCopyLink(share: ShareInfo): void {
    if (share.shareType === 'link' && share.shareId) {
      const fullLink = getShareableLink(share.shareId);
      this.clipboard.copy(fullLink);
      this.utilService.openSnackBar(
        'Link copied to clipboard!',
        'Success',
        'success'
      );
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
